"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

// The ONE sanctioned home for the Google Analytics (gtag.js) vendor host —
// mirrors the tile/NL/sync vendor-adapter allowlist pattern in
// scripts/lint-brand.ts. The concrete request host can't be a wildcard here (a
// <script src> is a real fetch), so this file is allowlisted for the literal.
const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Google Analytics 4 (gtag.js) loader. Carried over from the legacy WordPress
 * mapize.com (which fed GA4 property G-EH2V2DKH6E) so pageview / Search Console
 * / Ads continuity survives the rebuild cutover. PostHog stays the product-
 * analytics system of record — GA is purely additive for marketing/SEO
 * continuity. Everything else the old site loaded (Hotjar, session recording)
 * is covered better by PostHog and is intentionally NOT reproduced here.
 *
 * Gated on NEXT_PUBLIC_GA_MEASUREMENT_ID: unset (dev/preview/test) renders
 * nothing, exactly like PosthogBootstrap no-ops without its key — so no GA
 * cookies or beacons on unconfigured environments. Skips /embed/* for the same
 * reason PostHog does: embeds render on third-party sites where running our
 * analytics is unwanted and pure bundle weight.
 *
 * SPA pageviews: gtag does NOT hook the History API the way posthog-js's
 * "history_change" does. `gtag('config', ...)` fires exactly one page_view when
 * gtag.js loads (reading the live document.location, so no race), and this
 * component owns every SUBSEQUENT soft-navigation page_view via the pathname
 * effect — the `firstLoad` ref skips the initial pathname so the config-fired
 * view isn't double-counted. gtag reads document.location/title at event time,
 * so query strings ride along without useSearchParams (which would force this
 * subtree into a Suspense boundary and deopt static rendering).
 */
export function GoogleAnalytics() {
  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  const pathname = usePathname();
  const disabled = !measurementId || (pathname?.startsWith("/embed") ?? false);
  const firstLoad = useRef(true);

  useEffect(() => {
    if (disabled) return;
    // The initial page_view is fired by gtag('config') on load; only the
    // App Router soft navigations after it are ours to send.
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    window.gtag?.("event", "page_view");
  }, [disabled, pathname]);

  if (disabled) return null;

  return (
    <>
      <Script src={`${GTAG_SRC}?id=${measurementId}`} strategy="afterInteractive" />
      <Script id="ga-init" strategy="afterInteractive">
        {`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${measurementId}');`}
      </Script>
    </>
  );
}
